import { CalendarToday } from '@mui/icons-material';
import { Box, Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import MainCard from 'ui-component/cards/MainCard';
import Blocked from '../../assets/images/svg/Blocked.svg';
import LeftArrow from '../../assets/images/svg/left-arrow.svg';
import StackOfCoins from '../../assets/images/svg/StackofCoins.svg';
import TotalSales from '../../assets/images/svg/TotalSales.svg';
import UpDown from '../../assets/images/svg/Up-Down.svg';
import Logs from './Logs';
import TransactionHistoryList from './TransactionHistoryList';
import { useEffect, useMemo, useState } from 'react';
import getAxiosInstance from 'api';
import toast from 'react-hot-toast';
import moment from 'moment';
import { DateRangePicker } from 'rsuite';

const status = [
  {
    value: 'date',
    label: 'This Month',
  },
  {
    value: 'month',
    label: 'This Year',
  },
];

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.secondary[200],
  borderRadius: '10px',
  width: '52px',
  height: '52px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledMainCard = styled(MainCard)(({ theme }) => ({
  height: '200px',
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledCallCard = styled(MainCard)(({ theme }) => ({
  height: '430px',
  border: `1px solid ${theme.palette.divider}`,
}));

const DateRangeButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 400,
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));
/** Credit Risk Assessment product (affordability API). */
function isCreditRiskAssessment(product) {
  return (
    product?.productCode === 'affordability_full' ||
    product?.productName === 'Credit Risk Assessment'
  );
}

/**
 * For Credit Risk Assessment with free allowance: show freeAllowanceBalance until 0, then walletBalance.
 * Same as API Dashboard wallet (src/views/wallet/index.js).
 */
function getAvailableUnitDisplay(product) {
  if (!product) return 0;
  if (!isCreditRiskAssessment(product) || !product.isFreeAllowanceAllowed) {
    return product.walletBalance ?? 0;
  }
  const freeAllowanceBalance = product.freeAllowanceBalance;
  if (freeAllowanceBalance == null || Number(freeAllowanceBalance) === 0) {
    return product.walletBalance ?? 0;
  }
  return freeAllowanceBalance;
}

function getUnitsByProductCode(products, productCode) {
  const product = products?.find((p) => p.productCode === productCode);
  return getAvailableUnitDisplay(product);
}

const BackButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#fff',
  color: '#000',
  borderRadius: '50%',
  textTransform: 'none',
  fontWeight: 400,
  padding: '4px',
  minWidth: '32px',
  height: '32px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));
const BusinessDetails = () => {
  const theme = useTheme();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [businessPartner, setBusinessPartner] = useState();
  const [walletDetails, setWalletDetails] = useState();
  const [businessStatic, setBusinessStatic] = useState();
  const [selectedDate, setSelectedDate] = useState([
    moment().startOf('month').toDate(),
    moment().toDate(),
  ]);
  const navigate = useNavigate();

  const activeUnitsDisplay = useMemo(() => {
    const products = businessPartner?.products || [];
    const affordability_full = getUnitsByProductCode(products, 'affordability_full');
    const bvn = getUnitsByProductCode(products, 'bvn');
    const directDebit = getUnitsByProductCode(products, 'directDebit');
    return {
      affordability_full,
      bvn,
      directDebit,
      total: affordability_full + bvn + directDebit,
    };
  }, [businessPartner?.products]);

  useEffect(() => {
    fetchUserData();
    fetchUserCall();
  }, []);
  useEffect(() => {
    fetchWalletData();
    fetchUserCall();
  }, [selectedDate]);
  const fetchUserData = async () => {
    getAxiosInstance(
      `adminOrchestrator/dashboard/getBusinessPartnerById`,
      'post',
      {
        businessPartnerId: id,
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setBusinessPartner(res?.data?.data);
        } else {
          setBusinessPartner(null);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Error fetching data');
      })
      .finally(() => setLoading(false));
  };
  const fetchWalletData = async () => {
    getAxiosInstance(
      `adminOrchestrator/dashboard/getBusinessPartnerWalletDetails`,
      'post',
      {
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
        businessPartnerId: id,
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {   
          setWalletDetails({
            usedUnits: res?.data?.data?.usedUnits,
          });
        } else {
          setWalletDetails(null);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Error fetching data');
      })
      .finally(() => setLoading(false));
  };
  const fetchUserCall = async () => {
    getAxiosInstance(
      `adminOrchestrator/dashboard/getConsentCallCountsByBusinessPartner`,
      'post',
      {
        businessPartnerId: id,
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setBusinessStatic(res?.data?.data);
        } else {
          setBusinessStatic(null);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Error fetching data');
      })
      .finally(() => setLoading(false));
  };
  return (
    <>
      <Stack
        direction="row"
        alignItems={'baseline'}
        justifyContent={'space-between'}
        flexDirection={'row'}
        mb={2}
      >
        <Box display="flex" alignItems="baseline">
          <BackButton
            direction="row"
            alignItems="center"
            onClick={() => navigate(-1)}
          >
            <Box component={'img'} src={LeftArrow} />
          </BackButton>
          <Stack mr={4}>
            <Typography variant="h3">{businessPartner?.companyName}</Typography>
            <Typography variant="h5">
              {businessPartner?.emailAddress}
            </Typography>
            <Typography variant="h5">
              Registered on:
              {businessPartner?.createdAt &&
                moment(businessPartner?.createdAt).format('MMM DD, YYYY')}{' '}
            </Typography>
            <Typography variant="h5" color={theme.palette.secondary.dark}>
              Direct debit payment method : 0.4% + 100 (click to change)
            </Typography>
          </Stack>
        </Box>
        <Stack>
          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            value={selectedDate}
            onChange={(value) => setSelectedDate(value)}
            showOneCalendar
          />
        </Stack>
      </Stack>
      {console.log('businessPartner', businessPartner)}
      <Box>
        <Grid container spacing={3}>
          {/* Left side - Two big cards */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledCallCard>
                  <Stack direction="column" spacing={3}>
                    <IconWrapper>
                      <Box component={'img'} src={UpDown} />
                    </IconWrapper>
                    <Stack spacing={1}>
                      <Typography variant="h3">
                        {businessStatic?.successConsentRequest +
                          businessStatic?.successMandateRequest +
                          businessStatic?.successBVNRequest +
                          businessStatic?.blockedRequestCount +
                          businessStatic?.failedRequestCount | 0}
                      </Typography>
                      <Box>
                        <Typography
                          variant="body1"
                          fontSize={18}
                          marginBottom={3}
                        >
                          Total Calls
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider mt={3} />
                    <Stack>
                      <Typography variant="body2" fontSize={18}>
                        Credit Assessment:{' '}
                        {businessStatic?.successConsentRequest}
                      </Typography>
                      <Typography variant="body2" fontSize={18}>
                        BVN: {businessStatic?.successBVNRequest}
                      </Typography>
                      <Typography variant="body2" fontSize={18}>
                        Direct Debit: {businessStatic?.successMandateRequest}
                      </Typography>
                    </Stack>
                  </Stack>
                </StyledCallCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledCallCard>
                  <Stack direction="column" spacing={3}>
                    <IconWrapper>
                      <Box component={'img'} src={StackOfCoins} />
                    </IconWrapper>
                    <Stack spacing={1}>
                      <Typography variant="h3">
                        {activeUnitsDisplay?.total}
                      </Typography>
                      <Typography variant="body1" fontSize={18}>
                        Active Units
                      </Typography>
                      <Typography variant="body1" textAlign={'end'}>
                        Last updated 03/01/2024
                      </Typography>
                    </Stack>
                    <Divider mt={3} />
                    <Stack>
                      <Typography variant="body2" fontSize={18}>
                        Credit Assessment: {activeUnitsDisplay?.affordability_full}
                      </Typography>
                      <Typography variant="body2" fontSize={18}>
                        BVN: {activeUnitsDisplay?.bvn}
                      </Typography>
                      <Typography variant="body2" fontSize={18}>
                        Direct Debit: {activeUnitsDisplay?.directDebit}
                      </Typography>
                    </Stack>
                  </Stack>
                </StyledCallCard>
              </Grid>
            </Grid>
          </Grid>

          {/* Right side - Four small cards */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledMainCard>
                  <Stack direction="column" spacing={3}>
                    <IconWrapper>
                      <Box component={'img'} src={TotalSales} />
                    </IconWrapper>
                    <Stack spacing={1}>
                      <Typography variant="h3">
                        {walletDetails?.usedUnits}
                      </Typography>
                      <Typography variant="body1" fontSize={18}>
                        Units Used
                      </Typography>
                    </Stack>
                  </Stack>
                </StyledMainCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledMainCard>
                  <Stack direction="column" spacing={3}>
                    <IconWrapper>
                      <Box component={'img'} src={Blocked} />
                    </IconWrapper>
                    <Stack spacing={1}>
                      <Typography variant="h3">
                        {businessStatic?.blockedRequestCount}
                      </Typography>
                      <Typography variant="body1" fontSize={18}>
                        Blocked Requests
                      </Typography>
                    </Stack>
                  </Stack>
                </StyledMainCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledMainCard>
                  <Stack direction="column">
                    <IconWrapper>
                      <Box component={'img'} src={TotalSales} />
                    </IconWrapper>
                    <Stack spacing={1}>
                      <Typography variant="h3">
                        {businessStatic?.successConsentRequest +
                          businessStatic?.successMandateRequest +
                          businessStatic?.successBVNRequest || 0}
                      </Typography>
                      <Typography variant="body1" fontSize={18}>
                        Successful Requests
                      </Typography>
                    </Stack>
                  </Stack>
                </StyledMainCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledMainCard>
                  <Stack direction="column">
                    <IconWrapper>
                      <Box component={'img'} src={TotalSales} />
                    </IconWrapper>
                    <Stack spacing={1}>
                      <Typography variant="h3">
                        {businessStatic?.failedRequestCount}
                      </Typography>
                      <Typography variant="body1" fontSize={18}>
                        Failed Requests
                      </Typography>
                    </Stack>
                  </Stack>
                </StyledMainCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Logs selectedDate={selectedDate} />
      <TransactionHistoryList selectedDate={selectedDate} />
    </>
  );
};

export default BusinessDetails;
